<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import Button from 'primevue/button'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import CatalogForm from '@/views/catalog/CatalogForm.vue'
import { useAuthStore } from '@/stores/auth'
import { idFromParam, kindFromRoute, useCatalogRecord } from '@/composables/useCatalogRecord'
import { productApi, setApi, type CatalogFormPayload } from '@/services/catalog'

/**
 * Edición de un producto o un set.
 *
 * El tipo no se cambia al editar, por lo mismo que no se cambia al crear: lo dice
 * la ruta. Permitirlo convertiría un producto en un set o al revés, y el
 * identificador significaría otra cosa según quién lo mirara, sin que haya ningún
 * endpoint que haga esa migración.
 */

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const { record, loading, errorMessage, load } = useCatalogRecord()

const allowed = computed(() => authStore.hasPermission('catalog.manage'))
const saving = ref(false)
const serverErrors = ref<Record<string, string[]>>({})
const saveError = ref<string | null>(null)

const id = computed(() => idFromParam(route.params.id))
/**
 * El tipo lo dice la ruta: `/catalog/sets/5` es un set sin mirar nada más. Antes
 * venía de `?kind=`, que había que propagar por todos los enlaces.
 */
const kindHint = computed(() => kindFromRoute(route.meta.catalogKind))

/** A qué pantalla del catálogo se vuelve: la del tipo que se está viendo. */
const kindPlural = computed(() => (kindHint.value === 'set' ? 'sets' : 'products'))

/** El formulario se alimenta de los strings del recurso, no de números. */
const formValues = computed(() => {
  if (record.value === null) return undefined
  return {
    reference: record.value.reference,
    name: record.value.name,
    retail_price: record.value.retail_price,
    wholesale_price: record.value.wholesale_price,
    type: record.value.type ?? 'nacional',
  }
})

function extractServerErrors(cause: unknown): Record<string, string[]> {
  const maybeAxios = cause as {
    validationErrors?: Record<string, string[]>
    response?: { data?: { error?: { details?: { fields?: Record<string, string[]> } } } }
  }
  return maybeAxios.validationErrors ?? maybeAxios.response?.data?.error?.details?.fields ?? {}
}

function extractMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudieron guardar los cambios.'
}

async function fetchRecord(): Promise<void> {
  saveError.value = null
  if (id.value === null) return
  await load(id.value, kindHint.value)
}

onMounted(fetchRecord)
watch([id, kindHint], fetchRecord)

/** Vuelve al detalle del registro si se conoce, y al listado si no. */
function goBack(): void {
  if (record.value === null) {
    void router.push({ name: `catalog.${kindPlural.value}.index` })
    return
  }
  void router.push({ name: `catalog.${record.value.kind === 'set' ? 'sets' : 'products'}.show`, params: { id: String(record.value.id) } })
}

async function handleSubmit(payload: CatalogFormPayload): Promise<void> {
  if (record.value === null) return

  saving.value = true
  serverErrors.value = {}
  saveError.value = null

  try {
    // Igual que en el alta, `kind` y `type` son discriminantes del cliente y no
    // viajan en el cuerpo: `type` solo se reenvía si el registro es un set.
    const { kind, type, ...fields } = payload

    if (kind === 'set') {
      await setApi.update(record.value.id, { ...fields, type: type ?? record.value.type ?? 'nacional' })
    } else {
      await productApi.update(record.value.id, fields)
    }

    await router.push({ name: `catalog.${kind === 'set' ? 'sets' : 'products'}.show`, params: { id: String(record.value.id) } })
  } catch (cause: unknown) {
    serverErrors.value = extractServerErrors(cause)
    saveError.value = Object.keys(serverErrors.value).length > 0 ? null : extractMessage(cause)
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="min-w-0">
        <h1 class="truncate text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Editar {{ record?.kind === 'set' ? 'set' : 'producto' }}
        </h1>
        <p
          v-if="record"
          class="mt-1 font-mono text-sm text-surface-500 dark:text-surface-400"
        >
          {{ record.reference }}
        </p>
      </div>
      <Button
        label="Cancelar"
        icon="pi pi-arrow-left"
        severity="secondary"
        :disabled="saving"
        @click="goBack"
      />
    </header>

    <Message
      v-if="!allowed"
      severity="warn"
      :closable="false"
      variant="simple"
      role="alert"
    >
      No tienes permiso de gestión del catálogo, así que no puedes modificar registros.
    </Message>

    <Message
      v-if="errorMessage"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ errorMessage }}
    </Message>

    <Message
      v-if="saveError"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ saveError }}
    </Message>

    <AppCard v-if="loading">
      <div class="space-y-4">
        <Skeleton
          v-for="index in 4"
          :key="index"
          height="1.25rem"
        />
      </div>
    </AppCard>

    <CatalogForm
      v-else-if="record && allowed"
      :kind="record.kind"
      kind-locked
      :values="formValues"
      submit-label="Guardar cambios"
      :loading="saving"
      :server-errors="serverErrors"
      @submit="handleSubmit"
      @cancel="goBack"
    />
  </div>
</template>
